import { useState, useEffect, useRef } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '@/auth/hooks/useAuth';
import api, { ArcheMetadata } from '@/lib/api';
import { readArcheChannel } from '@/lib/test-tenant';
import { trackArcheOpenOnce } from '@/arche/lib/track-arche-open';
import { isSiteArcheSlug } from '@/arche/site-arches';
import { canonicalPluginArcheSegment } from '@/lib/arche-url';
import { archeNeedsPageLoad, loadArchePage, shownByFrontDoor } from '@/lib/page-manifest';
import { usePageManifestIdentity } from '@/components/PageManifestLink';
import { ArcheRouteStatusContext, type ArcheRouteStatus } from '@/arche/lib/arche-route-status';

/**
 * `children` under the route-status context. EVERY branch that renders the
 * page renders it through this, so the element at that position keeps one
 * type and React keeps the page mounted while the status flips — the same
 * reason the branches keep `children` first in their fragments.
 */
function page(children: React.ReactNode, status: ArcheRouteStatus) {
  return (
    <ArcheRouteStatusContext.Provider value={status}>{children}</ArcheRouteStatusContext.Provider>
  );
}

interface ConditionalProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * A conditional route protection component for arches.
 *
 * - Public arches (isPublic=true) can be accessed without authentication
 * - Non-public arches require authentication
 *
 * This component fetches arche metadata to determine if the arche is public
 * before deciding whether to require authentication.
 */
export function ConditionalProtectedRoute({ children }: ConditionalProtectedRouteProps) {
  const { id: archeId } = useParams<{ id: string }>();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const location = useLocation();
  // The Forge test channel (`?channel=test`) must reach a DRAFT arche's page:
  // the metadata route admits the owner and collaborators only on that
  // channel, so the guard has to ask on it (ADR-023 D4).
  const channel = readArcheChannel(location.search);

  const [archeMetadata, setArcheMetadata] = useState<ArcheMetadata | null>(null);
  const [metadataLoading, setMetadataLoading] = useState(true);
  const [metadataError, setMetadataError] = useState<string | null>(null);

  // Track which archeId the current metadata belongs to.
  // This prevents stale metadata from triggering the slug redirect
  // during the render before the useEffect resets state.
  const metadataForArcheIdRef = useRef<string | null>(null);
  // Whether the last decision rendered `children` for a signed-in user — see
  // the re-check branch below. Written during render, next to each return.
  const childrenShownRef = useRef(false);

  useEffect(() => {
    setMetadataLoading(true);
    setMetadataError(null);
    setArcheMetadata(null);
    metadataForArcheIdRef.current = null;

    if (!archeId) {
      setMetadataLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchMetadata() {
      try {
        const metadata = await api.getArcheMetadata(archeId!, channel ? { channel } : undefined);
        if (!cancelled) {
          setArcheMetadata(metadata);
          setMetadataError(null);
        }
      } catch (err) {
        if (!cancelled) {
          // If we get a 404, treat it as not found
          // If we get a 401, the arche exists but requires auth
          setMetadataError(err instanceof Error ? err.message : 'Failed to load arche');
          setArcheMetadata(null);
        }
      } finally {
        if (!cancelled) {
          // Record that this archeId has been resolved (success or failure) so the
          // stale check below releases. Without this, an errored fetch would leave
          // metadataForArcheIdRef null and strand the component on "Loading…".
          metadataForArcheIdRef.current = archeId!;
          setMetadataLoading(false);
        }
      }
    }

    fetchMetadata();

    return () => {
      cancelled = true;
    };
  }, [archeId, channel]);

  // Metadata is stale if it was fetched for a different archeId.
  // This check is synchronous (ref, not state) so it catches the render
  // that happens before the useEffect resets state.
  const metadataIsStale = metadataForArcheIdRef.current !== archeId;

  // The segment this URL should be rewritten to, or null when it is already
  // canonical — see `canonicalArcheSegment` below.
  const redirectSegment =
    archeId && archeMetadata && !metadataIsStale
      ? canonicalArcheSegment(archeId, archeMetadata)
      : null;

  // Increment the arche's "Times Used" counter on successful open. Deduped
  // per tab-session (client) and per user-arche 30-min window (server).
  // Only fires when the user will actually see the arche — skips the
  // canonicalizing redirects (the arche is counted once, on the URL it lands
  // on) and the login-bounce paths. Also skipped for anonymous
  // viewers of public arches: the endpoint requires auth and dedupes by
  // userId, so calling it without a session 401s and would trip the global
  // session-expired modal even though the user was never logged in.
  useEffect(() => {
    if (!archeMetadata || metadataIsStale) return;
    if (!isAuthenticated) return;
    // A redirecting render is a waypoint, not a destination. Site arches are
    // never slug-redirected, so a slug URL for one counts as an open.
    if (redirectSegment !== null) return;
    trackArcheOpenOnce(archeMetadata.archId);
  }, [archeMetadata, isAuthenticated, metadataIsStale, redirectSegment]);

  // Every arche is an app "Add to Home Screen" can pin as itself — a game, or
  // Fias's own Chat (Tom, 2026-10-17: "I want to be able to make an icon for
  // chat on my iPad and iPhone").
  //
  // The arche this render is about to SHOW (the `settled` branches below)
  // names the page after itself: its name and icon go on the home screen. The
  // page the front door `/` sent a visitor on to stays Fias — its icon opens
  // `/` (`shownByFrontDoor`).
  //
  // Where "Add to Home Screen" pins only the page load — iPhone and iPad,
  // outside an installed app (`archeNeedsPageLoad`) — an arche shown on a
  // document that was loaded as some other page would pin the page the tab
  // first loaded (LitCraft, opened from Fias, "still doesn't give the correct
  // URL"), so it loads its own page instead: a reload of this history entry,
  // which keeps any handoff riding in its state. The page waits under the
  // spinner, so nothing of the arche starts twice and its visit is counted
  // once, by the load.
  const shownArche =
    !authLoading &&
    !metadataLoading &&
    !metadataIsStale &&
    archeMetadata !== null &&
    redirectSegment === null &&
    (archeMetadata.isPublic || isAuthenticated)
      ? archeMetadata
      : null;
  const namesThePage = shownArche !== null && !shownByFrontDoor() ? shownArche : null;
  usePageManifestIdentity(namesThePage?.name, namesThePage?.icons);
  const loadsItsOwnPage =
    !!archeId && shownArche !== null && archeNeedsPageLoad(archeId, shownArche);
  useEffect(() => {
    if (loadsItsOwnPage) loadArchePage();
  }, [loadsItsOwnPage]);

  const spinner = (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
        <p className="mt-4 text-muted-foreground">Loading...</p>
      </div>
    </div>
  );

  // Show loading while auth or metadata is being fetched
  if (authLoading || metadataLoading || metadataIsStale) {
    // Re-checking after one arche opened another (`:id` changed) for a user
    // who was already let through: keep `children` MOUNTED under the spinner
    // instead of replacing them. A signed-in user passes this gate for every
    // arche that is not a redirect or an error, so nothing is shown that the
    // decision could withhold — the arche page below defers to its own
    // metadata (a plugin renderer sits blank and `pending` until it arrives).
    // Unmounting here would recreate a plugin's <iframe>, and Chrome/Edge
    // start a recreated frame's audio suspended until the user clicks in it
    // (see the <iframe> note in PluginRenderer). Signed-out visitors keep the
    // plain spinner: for them the next arche may need a login instead.
    if (childrenShownRef.current && isAuthenticated && !authLoading) {
      // `pending`: the segment the page now sees is not decided yet — it may
      // be a raw id about to be rewritten, or refused (ArcheRouteStatusContext).
      return (
        <>
          {page(children, 'pending')}
          <div className="fixed inset-0 z-50">{spinner}</div>
        </>
      );
    }
    childrenShownRef.current = false;
    return spinner;
  }

  if (!archeId) {
    childrenShownRef.current = false;
    return <Navigate to="/" replace />;
  }

  // Canonicalize the URL segment (see `canonicalArcheSegment`). The redirect
  // carries EVERYTHING the arrival carried: the sub-path, the query string
  // (dropping it would silently turn a test-channel link into the production
  // page), the hash, and the navigation STATE — a handoff (`fiasAiPrefill`,
  // `fiasAiHandoffContext`) rides in `location.state`, and the bridge's
  // `open_arche` and the assistant both navigate by raw id, so a redirect
  // that dropped state would silently throw away every handoff into a plugin.
  if (redirectSegment !== null) {
    const subPath = location.pathname.replace(`/a/${archeId}`, '');
    const target = `/a/${redirectSegment}${subPath}${location.search}${location.hash}`;
    const redirect = <Navigate to={target} state={location.state} replace />;
    // One arche opening a PLUGIN by id, for a user who was already let
    // through: keep `children` mounted across the waypoint exactly as the
    // re-check spinner above does, so the plugin's <iframe> element survives
    // the switch. The page below holds the plugin `pending` while its URL is a
    // waypoint (GenericArchePage), so nothing loads until the canonical URL.
    // (The internal-arche redirect keeps its old behaviour: it lands on a
    // dedicated page component, not the iframe renderer.)
    if (childrenShownRef.current && isAuthenticated && !redirectSegment.startsWith('arche_')) {
      // `pending`: a waypoint. The visit is counted once, at the canonical
      // URL — the key a cold load of the same arche uses.
      return (
        <>
          {page(children, 'pending')}
          <div className="fixed inset-0 z-50">{spinner}</div>
          {redirect}
        </>
      );
    }
    childrenShownRef.current = false;
    return redirect;
  }

  childrenShownRef.current = false;

  // Loading the arche's own page (see `loadsItsOwnPage`): show nothing of it.
  if (loadsItsOwnPage) {
    return spinner;
  }

  // Metadata fetch failed - could be 404 or network error
  if (metadataError) {
    // If not authenticated, redirect to login (the arche might exist but require auth)
    if (!isAuthenticated) {
      return (
        <Navigate
          to="/auth/login"
          state={{ from: `${location.pathname}${location.search}` }}
          replace
        />
      );
    }
    // If authenticated but still got an error, show not found
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground">Not Found</h1>
          <p className="mt-2 text-muted-foreground">This arche could not be found.</p>
        </div>
      </div>
    );
  }

  // If arche is public, allow access regardless of auth state
  if (archeMetadata?.isPublic) {
    childrenShownRef.current = isAuthenticated;
    return <>{page(children, 'settled')}</>;
  }

  // Non-public arche requires authentication
  if (!isAuthenticated) {
    return (
      <Navigate
        to="/auth/login"
        state={{ from: `${location.pathname}${location.search}` }}
        replace
      />
    );
  }

  childrenShownRef.current = true;
  return <>{page(children, 'settled')}</>;
}

/**
 * The URL segment `/a/<segment>` should be rewritten to, or null when it is
 * already the canonical one. One rule per kind of arche:
 *
 * - **Plugin arches (`arc_*`) are shown by NAME.** A raw `/a/arc_<32hex>` URL
 *   becomes `/a/<slug>` (`canonicalPluginArcheSegment`), so the address bar
 *   reads the same however the user arrived — a link, the menu, the bridge's
 *   `open_arche`, or an assistant handoff, most of which only know the id.
 *   A slug URL is left alone. Third-party site arches are `arc_*` too, and
 *   their snapshot is stored under the slug, which is where this sends them.
 * - **Internal arches (`arche_*`) are shown by ID.** A slug URL becomes the
 *   `arche_` id, which ArcheInteractionPage's dispatch chain matches
 *   literally. Site arches are exempt: their slug is the shareable URL people
 *   see (that is the point of publishing an arche as a website) and the key
 *   its prerendered snapshot is stored under at the edge. Rewriting it to an
 *   arche_ id would both uglify the URL and miss the snapshot. Dispatch still
 *   works because the internal-arche registry is keyed by slug as well as id.
 *
 *   The exemption is driven by the SITE_ARCHES registry — the same list that
 *   decides what the build prerenders — and deliberately NOT by
 *   `arche_type = 'site'`. That column is the marketplace taxonomy: the Arche
 *   Store and Learn About Fias carry it too, and exempting them would strand
 *   /a/arche-store and /a/learn-about-fias-resources on GenericArchePage (the
 *   plugin iframe renderer), which cannot resolve a bundle for an internal
 *   arche.
 *
 * The metadata is only ever the caller's own: the route answered 200, so the
 * access decision (published, restricted audience, lifecycle) already
 * admitted them. A caller the arche is hidden from got the unknown-id 404 and
 * never sees a slug.
 *
 * Exported for unit tests.
 */
export function canonicalArcheSegment(
  segment: string,
  metadata: Pick<ArcheMetadata, 'archId' | 'slug'>,
): string | null {
  if (metadata.archId.startsWith('arche_')) {
    return !isSiteArcheSlug(segment) && metadata.archId !== segment ? metadata.archId : null;
  }
  return canonicalPluginArcheSegment(segment, metadata);
}
